import React from "react";
import { pdf } from "@react-pdf/renderer";
import { computeUnits, strategyFromProposal } from "../strategy/StrategyData";
import {
    ProposalDetail,
    ProposalExamplesResponse,
    ProposalPricingPreviewResponse,
} from "../types";
import { ProposalPdf, ProposalPdfInput } from "./ProposalPdf";

export type { ProposalPdfInput } from "./ProposalPdf";

// Callers hand us the raw examples payload; images are prefetched into data
// URLs here because @react-pdf fetches images itself and cross-origin
// CDN/avatar hosts without CORS headers would fail the whole render. A
// failed image just renders as a text-only card.
export type ProposalPdfRenderInput = ProposalPdfInput & {
    examples?: ProposalExamplesResponse | null;
};

const blobToDataUrl = (blob: Blob): Promise<string> =>
    new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(String(reader.result || ""));
        reader.onerror = reject;
        reader.readAsDataURL(blob);
    });

const fetchDataUrl = async (url: string | null): Promise<string | null> => {
    if (!url) return null;
    try {
        const res = await fetch(url, { mode: "cors" });
        if (!res.ok) return null;
        return await blobToDataUrl(await res.blob());
    } catch {
        return null;
    }
};

async function resolveExamples(input: ProposalPdfRenderInput): Promise<ProposalPdfInput> {
    const { examples, ...rest } = input;
    if (!examples) return rest;
    const [useCases, experts] = await Promise.all([
        Promise.all(
            examples.videos.slice(0, 3).map(async (v) => ({
                title: v.title,
                contentType: v.content_type ? v.content_type.display_name : null,
                brandLine: [v.brand_name, v.product_title].filter(Boolean).join(" · ") || null,
                duration: v.length ? `${Math.round(v.length)} sec` : null,
                thumbnailDataUrl: await fetchDataUrl(v.thumbnail),
            })),
        ),
        Promise.all(
            examples.experts.slice(0, 3).map(async (e) => ({
                name: e.display_name,
                credential: e.credential,
                blurb: e.blurb,
                industryYears: e.industry_years,
                relevance: e.relevance || null,
                avatarDataUrl: await fetchDataUrl(e.avatar),
            })),
        ),
    ]);
    return { ...rest, useCases, experts };
}

// Builds the PDF input from a persisted proposal (detail pages). The
// optional preview fills the three-column Investment page; without it the
// non-selected tiers show "Custom quote".
export function pdfInputFromDetail(
    detail: ProposalDetail,
    opts: {
        internal: boolean;
        // Whether the render may show dollar figures (authenticated viewers
        // only — TDC-1563). Anonymous renders get the teaser line instead.
        showPricing: boolean;
        // TDC-1591: RMN renders pass false to suppress the "$20k" teaser
        // line too. Omitted (internal/anonymous callers) keeps the teaser.
        showPriceTeaser?: boolean;
        preview?: ProposalPricingPreviewResponse | null;
        shareUrl?: string | null;
        examples?: ProposalExamplesResponse | null;
    },
): ProposalPdfRenderInput {
    const strategy = strategyFromProposal(detail);
    return {
        strategy,
        proposal: {
            id: detail.id,
            name: detail.name,
            brandName:
                (detail.brand && detail.brand.name) ||
                (detail.proposal_meta && detail.proposal_meta.brand_name) ||
                null,
            contactEmail: detail.lead ? detail.lead.email : null,
            createdAt: detail.created_at,
            shareUrl:
                opts.shareUrl ||
                (detail.share_token
                    ? `${window.location.origin}/proposal/view/${detail.share_token}`
                    : null),
            pricingTotal: detail.pricing.total,
            auditUrl: detail.pdp_audit_report ? detail.pdp_audit_report.url : null,
            auditDate: detail.pdp_audit_report ? detail.pdp_audit_report.created_at : null,
        },
        tiers: opts.preview ? opts.preview.tiers : null,
        unitsByTier: {
            good: computeUnits(detail.objective, detail.secondary_objective, "good"),
            better: computeUnits(detail.objective, detail.secondary_objective, "better"),
            best: computeUnits(detail.objective, detail.secondary_objective, "best"),
        },
        examples: opts.examples || null,
        internal: opts.internal,
        showPricing: opts.showPricing,
        showPriceTeaser: opts.showPriceTeaser,
    };
}

const slugify = (s?: string | null) =>
    (s || "strategy")
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, "-")
        .replace(/(^-|-$)/g, "");

const renderBlob = async (input: ProposalPdfRenderInput) => {
    const resolved = await resolveExamples(input);
    return pdf(<ProposalPdf input={resolved} />).toBlob();
};

// Same anchor-download pattern as src/pages/sales/reportPdf.tsx.
export async function downloadProposalPdf(input: ProposalPdfRenderInput) {
    const blob = await renderBlob(input);
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${slugify(input.proposal.name) || "strategy"}.pdf`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
}

// Mirrors MAX_PDF_BASE64_BYTES on the API (emailProposalByToken): payloads
// past this get a 413, so callers pre-check and steer users to Download.
export const MAX_EMAIL_PDF_BASE64_CHARS = 8 * 1024 * 1024;

// Base64 (no data: prefix) for POST /public/proposals/:token/email — the
// same shape the pdp-audit email endpoint consumes.
export async function proposalPdfBase64(input: ProposalPdfRenderInput): Promise<string> {
    const blob = await renderBlob(input);
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = () => {
            const dataUrl = String(reader.result || "");
            resolve(dataUrl.slice(dataUrl.indexOf(",") + 1));
        };
        reader.onerror = reject;
        reader.readAsDataURL(blob);
    });
}
